import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAppTheme } from '../contexts/AppThemeContext'
import type { Application } from '../types/database'

// Webba ID default app key - this app should exist in the database
const WEBBA_ID_APP_KEY = 'wba_4b4662c4e2e9408a6c5d93908cdb057a4151a1528cc96288'

interface AppConfig {
  app: Application | null
  redirectUri: string | null
  loading: boolean
  error: string | null
  isDefaultApp: boolean
}

export function useAppConfig(): AppConfig {
  const [searchParams] = useSearchParams()
  const appKey = searchParams.get('app')
  const redirectUri = searchParams.get('redirect_uri')
  const { setBrandColor } = useAppTheme()

  const [app, setApp] = useState<Application | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDefaultApp, setIsDefaultApp] = useState(false)

  useEffect(() => {
    async function fetchApp() {
      setLoading(true)
      setError(null)

      // Use provided app key or fall back to Webba ID
      const keyToFetch = appKey || WEBBA_ID_APP_KEY

      const { data, error: fetchError } = await supabase
        .from('applications')
        .select('*')
        .eq('app_key', keyToFetch)
        .eq('is_active', true)
        .single()

      if (fetchError) {
        if (appKey) {
          // Only show error if user specified an app that doesn't exist
          setError('Application not found or inactive')
        }
        setApp(null)
        // Reset to default brand color
        setBrandColor('#A35BFF')
      } else {
        setApp(data)
        setIsDefaultApp(!appKey)
        // Apply the app's theme color
        if (data.theme_color) {
          setBrandColor(data.theme_color)
        }
      }

      setLoading(false)
    }

    fetchApp()

    // Reset to default brand color when leaving auth pages
    return () => {
      setBrandColor('#A35BFF')
    }
  }, [appKey, setBrandColor])

  return { app, redirectUri, loading, error, isDefaultApp }
}
